/**
 * Tela da Central de Apontamentos. Mesmo desenho das telas novas do Legado
 * (Área do PCP, Relatórios): lista limpa, texto neutro, cor só para estado
 * e tokens do tema (claro/escuro). As folhas em si têm cor própria (papel).
 */
import styled, { css } from "styled-components";

import { BP_MOBILE, BP_TABLET } from "../../styles/canonical/breakpoints";
import { PageContainer } from "../../styles/shared";

const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;
const desktop = `(min-width: ${BP_TABLET + 1}px)`;

export const SCRIM = "rgba(3, 4, 8, 0.6)";

export const Pagina = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 12px 12px 40px;

  @media ${tabletAcima} {
    padding: 16px 20px 40px;
    gap: 16px;
  }
`;

export const Topo = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
  min-width: 0;

  .titulo {
    flex: 1 1 260px;
    min-width: 0;
  }

  h1 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: var(--ink);
  }

  p {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--ink-2);
  }

  .acoes {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
`;

export const Contador = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
`;

const baseBotao = css`
  min-height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 80ms ease;

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 2px;
  }
`;

export const BotaoPrimario = styled.button`
  ${baseBotao};
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--ink-on-accent);
`;

export const BotaoSecundario = styled.button`
  ${baseBotao};
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink);
`;

export const BotaoTexto = styled.button`
  ${baseBotao};
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-2);
  padding: 0 8px;
`;

export const Abas = styled.nav`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border);

  a {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-2);
    text-decoration: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
  }

  a[aria-current="page"] {
    color: var(--ink);
    border-bottom-color: var(--accent);
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  min-width: 0;

  @media ${desktop} {
    grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
    align-items: start;
  }
`;

export const Coluna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const Bloco = styled.section`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  min-width: 0;

  > header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--border);
  }

  > header h2 {
    margin: 0;
    flex: 1 1 auto;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
  }

  > header .passo {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    background: var(--surface-2);
    color: var(--ink-2);
  }
`;

export const Busca = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 14px 8px;
  padding: 0 10px;
  min-height: 36px;
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink-3);

  input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: var(--ink);
  }

  &:focus-within {
    outline: 3px solid var(--accent-ring);
    outline-offset: 1px;
  }

  kbd {
    font-size: 11px;
    padding: 1px 5px;
    border-radius: 4px;
    border: 1px solid var(--border);
    color: var(--ink-3);
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 14px 12px;

  button {
    min-height: 30px;
    padding: 0 10px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink-2);
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  button[aria-pressed="true"] {
    border-color: var(--ink);
    background: var(--ink);
    color: var(--surface);
  }

  button:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 1px;
  }
`;

export const ListaDeClientes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 420px;
  overflow-y: auto;
  border-top: 1px solid var(--border);

  > li + li {
    border-top: 1px solid var(--border-subtle, var(--border));
  }
`;

export const LinhaCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${({ $lote }) => ($lote ? "7px 14px 7px 40px" : "9px 14px")};
  min-height: 40px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--surface-hover, var(--surface-2));
  }

  input {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--accent);
  }

  .principal {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    color: var(--ink);
    font-weight: ${({ $lote }) => ($lote ? 500 : 600)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .secundario {
    font-size: 12px;
    color: var(--ink-3);
    font-weight: 400;
  }

  .mono {
    font-family: var(--font-mono, ui-monospace, monospace);
  }
`;

export const Selo = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $tom }) => ($tom ? `var(--${$tom}-text)` : "var(--ink-3)")};

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $tom }) => ($tom ? `var(--${$tom}-solid)` : "var(--ink-4, var(--border-strong))")};
  }
`;

export const Vazio = styled.p`
  margin: 0;
  padding: 18px 14px;
  font-size: 13px;
  color: var(--ink-2);
`;

// --------------------------------------------------------------- matriz

export const Matriz = styled.div`
  padding: 10px 14px 14px;
  min-width: 0;
  user-select: none;
  touch-action: none;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 3px;
  }

  th {
    font-size: 11px;
    font-weight: 700;
    color: var(--ink-2);
    padding: 0;
  }

  th button {
    width: 100%;
    min-height: 28px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  th button:hover {
    background: var(--surface-2);
  }

  th.lote {
    width: 64px;
    text-align: left;
  }

  th.lote button {
    text-align: left;
    padding: 0 6px;
    font-family: var(--font-mono, ui-monospace, monospace);
    color: var(--ink);
  }

  td {
    padding: 0;
  }

  .presets {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }
`;

export const CelulaMatriz = styled.button`
  width: 100%;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${({ $ligada }) => ($ligada ? "var(--ink)" : "var(--border)")};
  background: ${({ $ligada }) => ($ligada ? "var(--ink)" : "var(--surface)")};
  color: ${({ $ligada }) => ($ligada ? "var(--surface)" : "var(--ink-3)")};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
    background: repeating-linear-gradient(45deg, var(--surface-2) 0 4px, var(--surface) 4px 8px);
    border-style: dashed;
    color: transparent;
  }

  &:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 1px;
  }
`;

// --------------------------------------------------------------- prévia

export const Previa = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 14px;
  min-width: 0;
`;

export const GrupoDoLote = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;

  > h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
  }

  > h3 span {
    font-weight: 400;
    color: var(--ink-3);
  }
`;

export const ListaDeAvisos = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: 12.5px;
    color: var(--ink);
    line-height: 1.4;
  }

  li::before {
    content: "";
    flex: 0 0 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--warning-solid);
    transform: translateY(-1px);
  }
`;

export const GradeDeMiniaturas = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  min-width: 0;
`;

export const Miniatura = styled.div`
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  cursor: zoom-in;
  text-align: left;

  &:hover {
    border-color: var(--border-strong);
    box-shadow: var(--shadow-1);
  }

  &:focus-visible {
    outline: 3px solid var(--accent-ring);
    outline-offset: 2px;
  }

  .moldura {
    position: relative;
    overflow: hidden;
  }

  .escala {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
  }
`;

export const LegendaDaMiniatura = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: var(--ink-2);
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: ${SCRIM};
  display: flex;
  flex-direction: column;

  .barra {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    color: #fff;
    font-size: 13px;
  }

  .barra .titulo {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .palco {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    display: flex;
    justify-content: center;
    padding: 0 14px 14px;
  }

  button {
    min-height: 34px;
    min-width: 34px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ------------------------------------------------------------ histórico

export const TabelaHistorico = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 9px 12px;
    text-align: left;
    border-bottom: 1px solid var(--border);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  th {
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-3);
  }

  td {
    color: var(--ink);
  }

  .mono {
    font-family: var(--font-mono, ui-monospace, monospace);
  }

  @media (max-width: ${BP_MOBILE}px) {
    .some-no-celular {
      display: none;
    }
  }
`;

export const Aviso = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--warning-border);
  background: var(--warning-bg);
  color: var(--warning-text);
  font-size: 13px;
`;
